import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiClient } from 'seacube-backend/src/api/client';
import { useSnapshot } from 'valtio';

import { useTheme } from '@mui/material/styles';

import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Fade,
  IconButton,
  Typography,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SendIcon from '@mui/icons-material/Send';

import { store as reeferInsightsStore } from '@/components/pages/ReeferInsights/store/store';

import { store } from '../store';
import {
  addAiMessage,
  addUserMessage,
  updateLastAiMessage,
} from '../store/chatbot';

const { VITE_GREENSEE_API_URL, VITE_AUTH0_AUDIENCE } = import.meta.env;

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  } 
  40% {
    transform: scale(1);
  }
`;

const TypingDots = () => (
  <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center', height: 20 }}>
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: '#999',
        animation: `${bounce} 1.4s infinite ease-in-out both`,
        animationDelay: '0s',
      }}
    />
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: '#999',
        animation: `${bounce} 1.4s infinite ease-in-out both`,
        animationDelay: '0.2s',
      }}
    />
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: '#999',
        animation: `${bounce} 1.4s infinite ease-in-out both`,
        animationDelay: '0.4s',
      }}
    />
  </Box>
);

// Add language mapping for speech recognition
const SPEECH_LANG_MAP: Record<string, string> = {
  en: 'en-US',
  fr: 'fr-FR',
  zh: 'zh-CN',
  es: 'es-ES',
  de: 'de-DE',
  it: 'it-IT',
  pt: 'pt-BR',
  ja: 'ja-JP',
  ko: 'ko-KR',
  ru: 'ru-RU',
  ar: 'ar-SA',
  hi: 'hi-IN',
  nl: 'nl-NL',
  sv: 'sv-SE',
  da: 'da-DK',
  no: 'nb-NO',
  fi: 'fi-FI',
  pl: 'pl-PL',
  cs: 'cs-CZ',
  hu: 'hu-HU',
  tr: 'tr-TR',
  th: 'th-TH',
  vi: 'vi-VN',
  id: 'id-ID',
  ms: 'ms-MY',
  tl: 'fil-PH',
  uk: 'uk-UA',
  bg: 'bg-BG',
  hr: 'hr-HR',
  sk: 'sk-SK',
  sl: 'sl-SI',
  et: 'et-EE',
  lv: 'lv-LV',
  lt: 'lt-LT',
  ro: 'ro-RO',
  el: 'el-GR',
  he: 'he-IL',
  fa: 'fa-IR',
  ur: 'ur-PK',
  bn: 'bn-BD',
  ta: 'ta-IN',
  te: 'te-IN',
  ml: 'ml-IN',
  kn: 'kn-IN',
  gu: 'gu-IN',
  mr: 'mr-IN',
  ne: 'ne-NP',
  si: 'si-LK',
  my: 'my-MM',
  km: 'km-KH',
  lo: 'lo-LA',
  ka: 'ka-GE',
  am: 'am-ET',
  sw: 'sw-TZ',
  zu: 'zu-ZA',
  af: 'af-ZA',
  is: 'is-IS',
  mt: 'mt-MT',
  cy: 'cy-GB',
  ga: 'ga-IE',
  eu: 'eu-ES',
  ca: 'ca-ES',
  gl: 'gl-ES',
  sq: 'sq-AL',
  mk: 'mk-MK',
  sr: 'sr-RS',
  bs: 'bs-BA',
  me: 'sr-ME',
  az: 'az-AZ',
  kk: 'kk-KZ',
  ky: 'ky-KG',
  uz: 'uz-UZ',
  mn: 'mn-MN',
  hy: 'hy-AM',
};

function LLMResponse({ text }: { text: string }) {
  const navigate = useNavigate();
  const theme = useTheme();

  // Regex to match container IDs
  const containerIdRegex = /[A-Z]{4}\d{7}/g;

  // Split text and replace container IDs with links
  const parts = text.split(containerIdRegex);
  const matches = text.match(containerIdRegex);

  const handleClick = (containerId: string) => {
    reeferInsightsStore.reeferId = containerId;
    navigate('/reefer-insights'); // adjust route as needed
  };

  return (
    <span>
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {part}
          {matches && matches[i] && (
            <a
              href="#"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.preventDefault();
                handleClick(matches[i]);
              }}
            >
              {matches[i]}
            </a>
          )}
        </React.Fragment>
      ))}
    </span>
  );
}

export const ChatWindow = ({ onClose }: { onClose: () => void }) => {
  const { t, i18n } = useTranslation();
  const [input, setInput] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const { conversationId, chatMessages } = useSnapshot(store);
  const { getAccessTokenSilently } = useAuth0();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [speechError, setSpeechError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const recognition = useRef<any | null>(null);
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([]);
  const [currentSpeechLang, setCurrentSpeechLang] = useState<string>('en-US');

  // Get speech language based on current i18n language
  const getSpeechLanguage = useCallback(() => {
    const currentLang = i18n.language.split('-')[0]; // Get base language (e.g., 'en' from 'en-US')
    return SPEECH_LANG_MAP[currentLang] || 'en-US';
  }, [i18n.language]);

  // Check supported languages
  useEffect(() => {
    if ('speechSynthesis' in window) {
      // Get available voices to determine supported languages
      const updateVoices = () => {
        const voices = speechSynthesis.getVoices();
        const languages = [...new Set(voices.map((voice) => voice.lang))];
        setSupportedLanguages(languages);
      };

      updateVoices();
      speechSynthesis.addEventListener('voiceschanged', updateVoices);

      return () => {
        speechSynthesis.removeEventListener('voiceschanged', updateVoices);
      };
    }
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition =
        // @ts-expect-error SpeechRecognition is not typed
        window.SpeechRecognition || window.webkitSpeechRecognition;

      recognition.current = new SpeechRecognition();
      recognition.current.continuous = false;
      recognition.current.interimResults = true;

      // Set language based on current i18n language
      const speechLang = getSpeechLanguage();
      recognition.current.lang = speechLang;
      setCurrentSpeechLang(speechLang);

      recognition.current.onstart = () => {
        setIsListening(true);
        setSpeechError(null);
      };

      recognition.current.onresult = (event: any) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          setInput((prev) => prev + finalTranscript);
          setInterimTranscript('');
        } else {
          setInterimTranscript(interimTranscript);
        }
      };

      recognition.current.onerror = (event: any) => {
        console.error('Speech recognition error', event.error);
        setIsListening(false);
        setInterimTranscript('');

        switch (event.error) {
          case 'no-speech':
            setSpeechError(t('chat.errors.noSpeech'));
            break;
          case 'audio-capture':
            setSpeechError(t('chat.errors.audioCapture'));
            break;
          case 'not-allowed':
            setSpeechError(t('chat.errors.notAllowed'));
            break;
          case 'language-not-supported':
            setSpeechError(
              t('chat.errors.languageNotSupported', {
                language: currentSpeechLang,
              }),
            );
            break;
          default:
            setSpeechError(t('chat.errors.speechGeneral'));
        }
      };

      recognition.current.onend = () => {
        setIsListening(false);
        setInterimTranscript('');
      };

      setSpeechSupported(true);
    }
  }, [t, getSpeechLanguage, currentSpeechLang]);

  // Update speech language when i18n language changes
  useEffect(() => {
    if (recognition.current) {
      const newSpeechLang = getSpeechLanguage();
      recognition.current.lang = newSpeechLang;
      setCurrentSpeechLang(newSpeechLang);
    }
  }, [i18n.language, getSpeechLanguage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const toggleListening = useCallback(() => {
    if (!recognition.current) {
      setSpeechError(t('chat.errors.notSupported'));
      return;
    }

    if (isListening) {
      recognition.current.stop();
    } else {
      setSpeechError(null);
      recognition.current.start();
    }
  }, [isListening, t]);

  const sendLlmMessage = async (prompt: string) => {
    if (!prompt.trim()) return;

    setIsLoading(true);
    addUserMessage(prompt);
    addAiMessage('...');

    try {
      const tokenResp = await getAccessTokenSilently({
        authorizationParams: {
          audience: VITE_AUTH0_AUDIENCE,
          redirect_uri: window.location.origin,
        },
        detailedResponse: true,
      });

      const accessToken = tokenResp.access_token;

      const client = new ApiClient({
        TOKEN: accessToken,
        BASE: VITE_GREENSEE_API_URL,
      });

      const body = {
        prompt,
        conversation_id: conversationId!,
      };

      const resp = await client.default.postEmissionDashboardAgent(body);
      const aiResponse = resp?.summary || t('chat.errors.noResponse');

      updateLastAiMessage(aiResponse);
    } catch (error) {
      console.error('Error sending message to AI agent', error);
      updateLastAiMessage(t('chat.errors.communication'));
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (input.trim()) {
      await sendLlmMessage(input);
      setInput('');
      setInterimTranscript('');
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      await sendMessage();
    }
  };

  const examplePrompts = [
    //which tradelanes are the least CO2 intensive
    t('chat.examples.emissionsDashboard.leastCO2IntensiveTradelanes'),
    //which tradelane has the highest transition operating mode % ?
    t('chat.examples.emissionsDashboard.mostHoursSpendInContainerZone'),
    // which brand has the lowest power requirement ?
    t('chat.examples.emissionsDashboard.lowestPowerRequirement'),
  ];

  const handleExampleClick = (prompt: string) => {
    sendLlmMessage(prompt);
  };

  const displayValue = input + interimTranscript;

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 35,
        right: 16,
        width: 400,
        height: 500,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 4,
        borderRadius: 2,
        backgroundColor: '#fff',
        zIndex: 1000,
      }}
    >
      {/* Header with language info */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1,
          borderBottom: '1px solid #ddd',
        }}
      >
        <Box>
          <Typography variant="h6" component="strong">
            {t('chat.title')}
          </Typography>
          {speechSupported && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              {t('chat.speechLanguage', { language: currentSpeechLang })}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Speech Error Alert */}
      {speechError && (
        <Fade in={!!speechError}>
          <Alert
            severity="warning"
            onClose={() => setSpeechError(null)}
            sx={{ m: 1 }}
          >
            {speechError}
          </Alert>
        </Fade>
      )}

      {/* Example Prompts */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          p: 1,
          borderBottom: '1px solid #eee',
        }}
      >
        {examplePrompts.map((prompt, index) => (
          <Chip
            key={index}
            label={prompt}
            onClick={() => handleExampleClick(prompt)}
            size="small"
            clickable
            disabled={isLoading}
          />
        ))}
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
        {chatMessages.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('chat.startConversation')}
          </Typography>
        ) : (
          chatMessages.map((msg, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                mb: 2,
              }}
            >
              <Box
                sx={{
                  maxWidth: '80%',
                  backgroundColor: msg.role === 'user' ? '#1976d2' : '#f5f5f5',
                  color: msg.role === 'user' ? 'white' : 'black',
                  borderRadius: '12px',
                  padding: '10px 15px',
                  wordBreak: 'break-word',
                }}
              >
                {msg.text === '...' && msg.role === 'ai' ? (
                  <TypingDots />
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    <LLMResponse text={msg.text} />
                  </Typography>
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  color: 'text.secondary',
                }}
              >
                {new Date(msg.timestamp).toLocaleTimeString()}
              </Typography>
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px',
          borderTop: '1px solid #ccc',
          gap: 1,
        }}
      >
        <Box sx={{ flex: 1, position: 'relative' }}>
          <input
            type="text"
            value={displayValue}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.inputPlaceholder')}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '12px',
              border: '1px solid #ccc',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              opacity: interimTranscript ? 0.7 : 1,
            }}
          />
          {isListening && (
            <Box
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="caption" color="primary">
                {t('chat.listening')}
              </Typography>
            </Box>
          )}
        </Box>

        {speechSupported && (
          <IconButton
            onClick={toggleListening}
            disabled={isLoading}
            sx={{
              color: isListening ? 'error.main' : 'inherit',
              '&:hover': {
                backgroundColor: isListening ? 'error.light' : 'action.hover',
              },
            }}
            title={
              isListening ? t('chat.stopListening') : t('chat.startListening')
            }
          >
            {isListening ? <MicOffIcon /> : <MicIcon />}
          </IconButton>
        )}

        <IconButton
          onClick={sendMessage}
          disabled={!input.trim() || isLoading}
          sx={{
            backgroundColor: '#1976d2',
            color: 'white',
            '&:hover': {
              backgroundColor: '#1565c0',
            },
            '&:disabled': {
              backgroundColor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
          title={t('chat.send')}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
